import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Radio,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import "moment/locale/th";

export default function SOSelectionDialog({
  open,
  onClose,
  soSelect,
  setSoSelect,
  soItems = [],
}) {
  const [selectedRadio, setSelectedRadio] = useState("none"); // "none" | "so"
  const [checkedItems, setCheckedItems] = useState([]); // ids ของ PO

  const maxSelect = 10;

  // sync radio กับ soSelect (มีค่า → โหมด "so", ว่าง → "none")
  useEffect(() => {
    if (!open) return;

    if (soSelect && soSelect.length > 0) {
      const ids = (soSelect ?? []).map((so) => so.purchaseOrderId);
      setCheckedItems(ids);
      setSelectedRadio("so");
    } else {
      setCheckedItems([]);
      setSelectedRadio("none");
    }
  }, [open, soSelect]);

  // Auto select PO ตาม soSelect เมื่อโหลดรายการ PO เสร็จ
  useEffect(() => {
    if (!open || soItems.length === 0) return;

    const autoCheckedIds = soItems
      .filter((so) => soSelect.some((s) => s.id === so.id))
      .map((so) => so.id);

    if (autoCheckedIds.length > 0) {
      setCheckedItems(autoCheckedIds);
      setSelectedRadio("so");
    }
  }, [open, soItems, soSelect]);

  const handleToggleCheck = (id, type) => {
    if (type === "so") {
      setCheckedItems((prev) => {
        if (prev.includes(id)) return prev.filter((v) => v !== id);
        if (prev.length < maxSelect) return [...prev, id];
        return prev;
      });
    } 
  };

  const handleSelectAll = (type, rows) => {
    const ids = rows.map((r) => Number(r.id));
    if (type === "so") setCheckedItems(ids);
  };

  // SO columns/rows
  const poColumns = [
    {
      field: "checkbox",
      headerName: "",
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={checkedItems.length === soRows.length && soRows.length > 0}
          indeterminate={
            checkedItems.length > 0 && checkedItems.length < soRows.length
          }
          onChange={(e) =>
            e.target.checked
              ? handleSelectAll("so", soRows)
              : setCheckedItems([])
          }
        />
      ),
      renderCell: (params) => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={checkedItems.includes(params.row.id)}
          onChange={() => handleToggleCheck(params.row.id, "so")}
        />
      ),
    },
    { field: "orderNumber", headerName: "SO #", width: 100 },
    { field: "detail", headerName: "รายละเอียด", width: 300 },
    { field: "quantity", headerName: "จำนวนสินค้า", width: 130 },
    { field: "startDate", headerName: "เริ่มต้น", width: 120 },
    { field: "endDate", headerName: "สิ้นสุด", width: 120 },
  ];

  const soRows = soItems.map((t) => ({
    id: t.id,
    orderNumber: t.id?.toString().padStart(4, "0"),
    detail: t.detail || "-",
    quantity: `${t.items?.length ?? 0} ไอเท็ม`,
    startDate: t.startDate
      ? moment(t.startDate).add(543, "year").format("D MMM YYYY")
      : "-",
    endDate: t.endDate
      ? moment(t.endDate).add(543, "year").format("D MMM YYYY")
      : "-",
  }));

  return (
    <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="lg">
      <DialogTitle>การล็อกราคาสินค้า</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          กรุณาเลือกประเภทการล็อกราคาสินค้าถ้าต้องการใช้กับตั๋วนี้
        </Typography>

        {/* PO */}
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            value="so"
            control={
              <Radio
                checked={selectedRadio === "so"}
                onChange={() => {
                  setCheckedItems([]);
                  setSelectedRadio("so");
                }}
              />
            }
            label={
              <Typography>
                ใบราคาสินค้า (SO) — {checkedItems.length}/{maxSelect}
              </Typography>
            }
          />
          {selectedRadio === "so" && (
            <Box
              sx={{
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 1,
                mt: 1,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography sx={{ mb: 1 }}>
                จำนวนรายการ: {soRows.length}
              </Typography>
              <DataGrid
                rows={soRows}
                columns={poColumns}
                autoHeight
                disableRowSelectionOnClick
                hideFooter
                sx={{ "& .MuiDataGrid-columnHeaders": { fontWeight: "bold" } }}
              />
            </Box>
          )}
        </Box>

        {/* None */}
        <Box>
          <FormControlLabel
            value="none"
            control={
              <Radio
                checked={selectedRadio === "none"}
                onChange={() => {
                  setCheckedItems([]);
                  setSelectedRadio("none");
                }}
              />
            }
            label="ไม่ใช้การล็อกราคา (ทั่วไป)"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 0 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => {
            if (selectedRadio === "so") {
              const selectedSOs = soItems.filter((so) =>
                checkedItems.includes(so.id)
              );
              setSoSelect(selectedSOs);
              onClose();
              return;
            } else if (selectedRadio === "none") {
              setSoSelect([]);
              onClose();
              return;
            }
          }}
          sx={{
            borderRadius: 0,
            py: 1.8,
            fontWeight: 700,
            bgcolor: "#1E2630",
            "&:hover": { bgcolor: "#111820" },
          }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
}
